/**
 * NPI and EIN format rules — used by the drawer's validation and by the mock generator.
 *
 * An NPI is ten digits whose last digit is a Luhn check over the card-issuer prefix `80840`
 * followed by the first nine (CMS, "Requirements for NPI check digit").
 */

export function rosterIsValidNpi(npi: string): boolean {
    if (!/^\d{10}$/.test(npi)) {
        return false;
    }
    return luhnSum(`80840${npi}`, false) % 10 === 0;
}

/** Append the check digit to a nine-digit NPI base. */
export function rosterNpiWithCheckDigit(base: string): string {
    if (!/^\d{9}$/.test(base)) {
        throw new Error(`An NPI base is nine digits, got "${base}"`);
    }
    const sum = luhnSum(`80840${base}`, true);
    return `${base}${(10 - (sum % 10)) % 10}`;
}

/** Nine digits, with or without the `XX-XXXXXXX` hyphen. */
export function rosterIsValidEin(ein: string): boolean {
    return /^\d{2}-?\d{7}$/.test(ein);
}

/**
 * Luhn sum from the right. `doubleFirst` is true when computing a check digit (the payload's
 * rightmost digit is doubled) and false when validating a number that already ends in one.
 */
function luhnSum(digits: string, doubleFirst: boolean): number {
    let sum = 0;
    for (let i = digits.length - 1, position = 0; i >= 0; i -= 1, position += 1) {
        let digit = Number(digits[i]);
        if (position % 2 === (doubleFirst ? 0 : 1)) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }
        sum += digit;
    }
    return sum;
}
